"use client";

import React, { useState } from "react";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { t } from "@/lib/site-texts";

interface Message {
  id: string;
  sender: "bot" | "user";
  text: string;
}

export default function ChatbotWidget() {
  const settings = useSiteSettings();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      sender: "bot",
      text: t("chatbot.greeting", "Hallo! Welkom bij Mijnondergoedwinkel.nl. Waar kan ik je vandaag mee helpen?"),
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  // Als de chatbot in het beheerpaneel is uitgeschakeld, toon deze dan niet op de website
  if (settings && settings.chatbotEnabled === false) {
    return null;
  }

  const handleSend = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;

    const userMsg: Message = { id: Date.now().toString(), sender: "user", text };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    setTimeout(() => {
      let botResponse = "";
      const lower = text.toLowerCase();

      if (lower.includes("status") || lower.includes("bestelling") || lower.includes("order") || lower.includes("track")) {
        botResponse = t("chatbot.answer.orderStatus", "Om de status van je bestelling op te zoeken kun je je ordernummer en e-mailadres hier invullen. Wij sturen direct de meest recente status en Track & Trace code!");
      } else if (lower.includes("retour") || lower.includes("terugsturen") || lower.includes("ruilen")) {
        botResponse = `Je hebt ${settings.returnDays} dagen de tijd om je artikelen op eigen kosten te retourneren. Aanmelden kan eenvoudig via je account of de pagina Bezorgen & Retourneren.`;
      } else if (lower.includes("verzending") || lower.includes("verzendkosten") || lower.includes("gratis")) {
        botResponse = `Binnen Nederland is verzending gratis bij bestellingen vanaf ${settings.shippingFreeFrom}! Voor orders onder ${settings.shippingFreeFrom} rekenen we ${settings.shippingCost} verzendkosten.`;
      } else {
        botResponse = t("chatbot.answer.fallback", "Bedankt voor je bericht! We helpen je zo snel mogelijk verder. Kom je er niet helemaal uit? Stuur gerust een e-mail naar info@mijnondergoedwinkel.nl.");
      }

      // Vraag opslaan in de backend (achtergrond)
      fetch("/api/admin/helpdesk-logs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: text, response: botResponse, timestamp: new Date().toISOString() }),
      }).catch(() => {});

      setMessages((prev) => [
        ...prev,
        { id: (Date.now() + 1).toString(), sender: "bot", text: botResponse },
      ]);
      setLoading(false);
    }, 600);
  };

  return (
    <>
      {/* Zwevende helpknop rechtsonder met huisstijlkleur (#720917 bordeauxrood) */}
      <button
        onClick={() => setOpen(!open)}
        style={{
          position: "fixed",
          bottom: "24px",
          right: "24px",
          zIndex: 9999,
          backgroundColor: "#720917",
          color: "#ffffff",
          border: "none",
          borderRadius: "50px",
          padding: "14px 22px",
          boxShadow: "0 4px 16px rgba(114, 9, 23, 0.35)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          fontWeight: 600,
          fontSize: "0.95rem",
          transition: "transform 0.2s ease",
        }}
        aria-label={t("chatbot.openButton.ariaLabel", "Chatbot openen")}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
        {open ? (
          <span data-t-key="chatbot.closeButton.label">{t("chatbot.closeButton.label", "Sluiten")}</span>
        ) : (
          <span data-t-key="chatbot.openButton.label">{t("chatbot.openButton.label", "Vraag stellen")}</span>
        )}
      </button>

      {/* Chatvenster */}
      {open && (
        <div
          style={{
            position: "fixed",
            bottom: "84px",
            right: "24px",
            width: "360px",
            maxWidth: "calc(100vw - 48px)",
            height: "480px",
            backgroundColor: "#ffffff",
            borderRadius: "16px",
            boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            border: "1px solid #e5e7eb",
          }}
        >
          {/* Header in huisstijl (#720917) */}
          <div
            style={{
              backgroundColor: "#720917",
              color: "#ffffff",
              padding: "16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontWeight: 700, fontSize: "1rem" }} data-t-key="chatbot.header.title">{t("chatbot.header.title", "Mijnondergoedwinkel Helpdesk")}</div>
              <div style={{ fontSize: "0.8rem", opacity: 0.9 }} data-t-key="chatbot.header.subtitle">{t("chatbot.header.subtitle", "Automatische klantenservice")}</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              style={{ background: "none", border: "none", color: "#ffffff", cursor: "pointer", fontSize: "1.2rem" }}
            >
              ✕
            </button>
          </div>

          {/* Berichtenlijst */}
          <div style={{ flex: 1, padding: "16px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "12px", background: "#fafafa" }}>
            {messages.map((m) => (
              <div
                key={m.id}
                data-t-key={m.id === "1" ? "chatbot.greeting" : undefined}
                style={{
                  alignSelf: m.sender === "user" ? "flex-end" : "flex-start",
                  backgroundColor: m.sender === "user" ? "#720917" : "#ffffff",
                  color: m.sender === "user" ? "#ffffff" : "#1a1a1a",
                  padding: "10px 14px",
                  borderRadius: "14px",
                  fontSize: "0.875rem",
                  lineHeight: "1.45",
                  maxWidth: "85%",
                  border: m.sender === "user" ? "none" : "1px solid #e5e5e5",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                }}
              >
                {m.text}
              </div>
            ))}
            {loading && (
              <div style={{ alignSelf: "flex-start", backgroundColor: "#ffffff", border: "1px solid #e5e5e5", padding: "10px 14px", borderRadius: "14px", fontSize: "0.85rem", color: "#888" }} data-t-key="chatbot.typingIndicator">
                {t("chatbot.typingIndicator", "Aan het typen...")}
              </div>
            )}
          </div>

          {/* Invoerbalk */}
          <form onSubmit={handleSend} style={{ display: "flex", padding: "12px", borderTop: "1px solid #e5e7eb", gap: "8px", background: "#fff" }}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("chatbot.input.placeholder", "Typ je vraag...")}
              style={{
                flex: 1,
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                padding: "9px 12px",
                fontSize: "0.875rem",
                outline: "none",
              }}
            />
            <button
              type="submit"
              style={{
                backgroundColor: "#720917",
                color: "#ffffff",
                border: "none",
                borderRadius: "8px",
                padding: "8px 16px",
                fontWeight: 600,
                cursor: "pointer",
                fontSize: "0.875rem",
              }}
              data-t-key="chatbot.submitButton.label"
            >
              {t("chatbot.submitButton.label", "Stuur")}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
